import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import { CasualTournamentEntity } from '@fantaz/shared/data-access/models/src/lib/tournament';
import { Game } from '@fantaz/shared/data-access/models/src/lib/game';
import { default as slugify } from 'slugify';
import { trigger, transition, style, animate } from '@angular/animations';

interface TournamentPriceRange {
  start: number;
  end: number;
}

@Component({
  selector: 'fz-game-card',
  templateUrl: './game-card.component.html',
  styleUrls: ['./game-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeSlideInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('500ms', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('500ms', style({ opacity: 0, transform: 'translateY(20px)' })),
      ]),
    ]),
  ],
})
export class GameCardComponent {
  // Identifier
  @Input()
  tournament?: CasualTournamentEntity;

  @Input()
  disableExtraData?: boolean;

  @Input()
  cardRightTag?: TemplateRef<any>;

  @Input()
  assetUrl? = '';

  @Output()
  selected = new EventEmitter();

  @Output()
  share = new EventEmitter();

  // Range of price
  @Input() priceRange: TournamentPriceRange = {
    start: 50.0,
    end: 100.0,
  };

  getGameImage(
    game: Game,
    type: 'avatar' | 'card' | 'background' = 'avatar',
  ): string {
    const result = game.key.split('.');
    const [gameName, version] = result || ['default', 'v1'];

    if (type === 'background') {
      return `${this.assetUrl}/shared/img/games/${gameName}/${version}/${gameName}-card.webp`;
    }

    return `${this.assetUrl}/shared/img/games/${gameName}/${version}/${gameName}-${type}.webp`;
  }

  get gameName(): string {
    let name = this.tournament?.game.name as string;
    name = 'game-' + name.toLowerCase();
    return slugify(name);
  }

  get getGameImageUrl(): string {
    return this.getGameImage(this.tournament?.game as Game);
  }

  get getBackgroundImageUrl(): string {
    if (
      this.tournament?.lifeExperience &&
      this.tournament.tournamentCardBackground
    ) {
      return this.tournament.tournamentCardBackground as string;
    }
    return this.getGameImage(this.tournament?.game as Game, 'card');
  }

  onClick(event: Event) {
    // stop propagation
    event.stopPropagation();
    this.selected.emit(this.tournament);
  }

  @HostListener('click', ['$event'])
  onShare(event: Event) {
    // stop propagation
    event.stopPropagation();
    this.share.emit(event);
  }
  cardBarckgroundImageUrl() {
    return (
      this.tournament?.tournamentCardBackground ||
      this.tournament?.game?.imageCoverUrl
    );
  }
}

//
// Redesign cards in components
//

/**
 * Share button of a game card, intended for use within `<fz-game-card>`. This
 * component is an optional convenience for one variety of card title; any
 * custom title element may be used in its place.
 *
 * FzGameCardShareActionDirective provides no behaviors, instead serving as a
 * purely visual treatment.
 */
// @Directive({
//   selector: `fz-game-card-share-action, [fz-game-card-share-action], [fzGameCardAction]`,
//   // eslint-disable-next-line @angular-eslint/no-host-metadata-property
//   host: {'class': 'fz-game-card__share-action'},
// })
// export class FzGameCardShareActionDirective {}

// <fz-game-card>
//   <fz-game-share-action onShare="onShare()"></fz-game-share-action>
// </fz-game-card>

/**
 * @deprecated Use fz-game-card-tags
 */
@Component({
  selector: `fz-game-card-type, [fz-game-card-type], [fzGameCardType]`,
  template: `<div class="fz-game-card__type">
    <img
      src="/shared/img/tournaments/tournament_types/{{ type ? type : '' }}.svg"
      alt="Tournament type"
    />
    <span>{{ typeName }}</span>
  </div>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FzGameCardTypeComponent {
  @Input()
  type: 'casual' | 'charity' | 'free' | 'prize' | 'zbo';

  get typeName(): string {
    const mapStr = {
      casual: 'Casual Tournament',
      charity: 'Charity Event',
      free: 'Free Tournament',
      prize: 'Prize Tournament',
      zbo: 'ZBO Tournament',
    };
    return mapStr[this.type] ? mapStr[this.type] : '';
  }
}

/**
 * Tags of a game card, intended for use within `<fz-game-card>`.
 *
 * FzGameCardTagsComponent provides no behaviors, instead serving as a
 * purely visual treatment.
 */
@Component({
  selector: `fz-game-card-tags, [fz-game-card-tags], [fzGameCardTags]`,
  templateUrl: './game-card-tags.component.html',
  styleUrls: ['./game-card-tags.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FzGameCardTagsComponent {
  @Input()
  tags?: string[];

  @Input()
  external? = false;

  @Input()
  variant?: 'text' | 'outlined' | 'contained' = 'contained';

  @Input()
  color?: 'primary' | 'accent' | 'warn' = 'primary';

  @Input()
  size?: 'small' | 'medium' | 'large' = 'medium';

  @Input()
  assetUrl? = '';

  priority = {
    'Charity Event': 1,
    'ZBO Only Tournament': 2,
    'Prize Tournament': 3,
    'Free Tournament': 4,
    'Cash Tournaments ': 5,
  };
}

@Component({
  selector: `fz-game-card-time, [fz-game-card-time], [fzGameCardTime]`,
  template: `<div class="fz-game-card__time">
    <div
      class="fz-game-card__time__progress"
      style="--data-percent:{{ progress }}%"
    ></div>
    <div class="date_info">
      <span class="fz-game-card__time__start_date">{{ start | date }}</span>
      <div *ngIf="start" class="fz-game-card__time__divider">
        <span></span>
        <span></span>
        <span></span>
      </div>
      <span class="fz-game-card__time__end_date">
        {{ end ? (end | date) : 'Not ending soon' }}
      </span>
    </div>
  </div>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FzGameCardTimeComponent {
  @Input()
  start?: Date;

  @Input()
  end?: Date;

  get progress(): number {
    const today = Date.now();
    if (this.start && this.end) {
      // convert date to date  in case of string
      const start =
        typeof this.start == 'string' ? new Date(this.start) : this.start;
      const end = typeof this.end == 'string' ? new Date(this.end) : this.end;

      // calculate percent
      const total = end.getTime() - start.getTime();
      const progress = today - start.getTime();

      return Math.round((progress / total) * 100);
    }

    return 0;
  }
}

/**
 *
 */
@Component({
  selector: `fz-game-card-prize, [fz-game-card-prize], [fzGameCardPrize]`,
  template: `<div class="fz-game-card__prize">
    <div class="fz-game-card__prize__title">
      Prize {{ range ? 'range' : '' }}
    </div>
    <div class="fz-game-card__prize__value">
      <fz-currency [value]="prize"></fz-currency> -
      <fz-currency [value]="prizeRangeEnd" *ngIf="range"></fz-currency>
    </div>
  </div>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FzGameCardPrizeComponent {
  @Input()
  prize?: number;

  @Input()
  prizeRangeEnd?: number;

  @Input()
  range?: boolean;
}

/**
 */
@Component({
  selector: `fz-game-card-prize-description, [fz-game-card-prize-description], [fzGameCardPrizeDescription]`,
  template: `<div class="fz-game-card__prize_description">
    <div class="fz-game-card__prize_description__inner">
      <div class="fz-game-card__prize_description__title ">
        <p class="two-lines">{{ content }}</p>
      </div>
      <div class="fz-game-card__prize_description__image_root">
        <div class="fz-game-card__prize_description__image_root__img">
          <img
            *ngIf="imgUrl"
            [src]="imgUrl"
            alt="Tournament prize image"
            lazy
          />
        </div>
      </div>
    </div>
  </div>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FzGameCardPrizeDescriptionComponent {
  @Input()
  content?: string;

  @Input()
  imgUrl?: string;
}
